import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Logger } from '../@shared/logger.service';

export interface Credentials {
  username: string;
  email: string;
  name: string;
  company: string;
  groups: string[];
}

const logger = new Logger('My-Logger');

const logAuthEvents = (event: string) => {
  switch (event) {
    case 'signedIn':
      logger.info('user signed in');
      break;
    case 'signedOut':
      logger.info('user signed out');
      break;
    case 'signInWithRedirect':
      logger.info('Cognito Hosted UI sign-in initiated');
      break;
    case 'signInWithRedirect_failure':
      logger.error('Cognito Hosted UI sign-in failed');
      break;
    case 'tokenRefresh':
      logger.info('token refresh succeeded');
      break;
    case 'tokenRefresh_failure':
      logger.error('token refresh failed');
      break;
    case 'customOAuthState':
      logger.info('custom state returned from CognitoHosted UI');
      break;
  }
};

@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private _credentials = signal<Credentials | null>(null);
  public readonly credentials = this._credentials.asReadonly();
  public readonly username = computed(() => {
    let ret = '';
    if (this.credentials() !== null) {
      ret = this.credentials()!.username;
    }

    return ret;
  });

  public readonly email = computed(() => {
    let ret = '';
    if (this.credentials() !== null) {
      ret = this.credentials()!.email;
    }

    return ret;
  });

  public readonly name = computed(() => {
    let ret = '';
    if (this.credentials() !== null) {
      ret = this.credentials()!.name;
    }

    return ret;
  });

  public readonly company = computed(() => {
    let ret = '';
    if (this.credentials() !== null) {
      ret = this.credentials()!.company;
    }

    return ret;
  });

  public readonly admin = computed(() => {
    let ret = false;
    if (this.credentials() !== null) {
      ret = this.credentials()!.groups.includes('admin');
    }

    return ret;
  });

  // Add method to check if user is authenticated
  public readonly isAuthenticated = computed(() => {
    return this.credentials() !== null;
  });

  // Add method to force clear credentials (useful for logout/session timeout)
  public clearCredentials(): void {
    console.log('Clearing credentials manually');
    this._credentials.set(null);
  }

  // Add method to manually refresh credentials
  public async refreshCredentials(): Promise<boolean> {
    try {
      this._credentials.set(await this.fetchCredentials());
      return true;
    } catch (error) {
      console.log('Failed to refresh credentials:', error);
      this._credentials.set(null);
      return false;
    }
  }

  constructor(private router: Router) {
    void this.initializeAuth();
    Hub.listen('auth', ({ payload }) => this.handleAuthEvent(payload.event));
  }

  private async initializeAuth() {
    try {
      this._credentials.set(await this.fetchCredentials());
    } catch (err) {
      console.log('error currentAuthenticatedUser: ' + err);
      this._credentials.set(null);
    }
  }

  private async fetchCredentials(): Promise<Credentials> {
    const [user, session, attributes] = await Promise.all([
      getCurrentUser(),
      fetchAuthSession(),
      fetchUserAttributes(),
    ]);
    const accessToken = session.tokens?.accessToken;
    if (!accessToken) {
      throw new Error('No valid access token is available');
    }

    const groupsClaim = accessToken.payload['cognito:groups'];
    const groups = Array.isArray(groupsClaim)
      ? groupsClaim.filter((group): group is string => typeof group === 'string')
      : [];

    return {
      username: user.username,
      email: attributes.email ?? '',
      groups,
      name: attributes.name ?? '',
      company: attributes.address || 'No Company',
    };
  }

  private handleAuthEvent(event: string) {
    logAuthEvents(event);
    switch (event) {
      case 'signedIn':
        void this.refreshCredentials().then((authenticated) => {
          if (authenticated) {
            this.router.navigate(['/home']);
          }
        });
        break;
      case 'signedOut':
        console.log('user signed out');
        this._credentials.set(null);
        this.router.navigate(['/home']);
        break;
      case 'tokenRefresh_failure':
        console.log('token refresh failed - clearing credentials');
        this._credentials.set(null);
        // Don't navigate here to avoid infinite loops
        break;
    }
  }
}
